
import React, { useState, useCallback, useMemo } from 'react';
import { Settings, Copy, RefreshCw, Check, Layout, Code, Eye, FileCode, Layers, Info } from 'lucide-react';
import { TextType, Theme, Framework, GeneratorConfig, PageStructure } from './types';
import { generateIpsum } from './services/contentService';

const App: React.FC = () => {
  const [config, setConfig] = useState<GeneratorConfig>({
    type: TextType.PAGE_LAYOUT,
    count: 3,
    theme: Theme.SAAS,
    framework: Framework.TAILWIND,
    startWithLorem: false,
    customWords: '',
    includeImages: true,
  });

  const [generatedResult, setGeneratedResult] = useState<string | PageStructure>('Configure the sidebar and click "Build Content" to generate local placeholder text...');
  const [viewMode, setViewMode] = useState<'preview' | 'code'>('preview');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [copyStatus, setCopyStatus] = useState<'none' | 'text' | 'html'>('none');

  const handleGenerate = async () => {
    setIsLoading(true);
    try {
      const result = await generateIpsum(config);
      setGeneratedResult(result);
      if (config.type !== TextType.PAGE_LAYOUT) {
        setViewMode('preview');
      }
    } catch (err) {
      setGeneratedResult("Generation failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const isPage = typeof generatedResult !== 'string';

  const generatedHTML = useMemo(() => {
    if (!isPage) return '';
    const data = generatedResult as PageStructure;
    const fw = config.framework;

    const generateFrameworkHTML = () => {
      switch (fw) {
        case Framework.BOOTSTRAP:
          return `<section class="container py-5 my-5">
  <header class="text-center mb-5">
    <h1 class="display-3 fw-bold mb-3">${data.title}</h1>
    <p class="lead text-muted mx-auto" style="max-width: 700px;">${data.heroSubtitle}</p>
  </header>

  ${data.sections.map((section, idx) => `
  <div class="row align-items-center mb-5 py-4 ${idx % 2 === 1 ? 'flex-row-reverse' : ''}">
    <div class="col-lg-6 mb-4 mb-lg-0">
      <h2 class="h1 fw-bold mb-4">${section.heading}</h2>
      ${section.content.map(p => `<p class="text-secondary mb-3">${p}</p>`).join('\n      ')}
      ${section.bullets ? `
      <ul class="list-unstyled mt-4">
        ${section.bullets.map(bullet => `
        <li class="mb-3 d-flex align-items-start">
          <span class="badge rounded-pill bg-primary mt-2 me-3" style="width: 10px; height: 10px; padding: 0;">&nbsp;</span>
          <span>${bullet}</span>
        </li>`).join('')}
      </ul>` : ''}
    </div>
    ${section.imageUrl ? `
    <div class="col-lg-6">
      <img src="${section.imageUrl}" alt="${section.heading}" class="img-fluid rounded shadow-lg w-100" />
    </div>` : ''}
  </div>`).join('\n')}
</section>`;

        case Framework.MODERN_CSS:
          return `<section class="landing-page">
  <header class="hero-header">
    <h1 class="hero-header__title">${data.title}</h1>
    <p class="hero-header__subtitle">${data.heroSubtitle}</p>
  </header>

  ${data.sections.map((section, idx) => `
  <article class="content-block ${idx % 2 === 1 ? 'content-block--reversed' : ''}">
    <div class="content-block__text">
      <h2 class="content-block__heading">${section.heading}</h2>
      ${section.content.map(p => `<p class="content-block__para">${p}</p>`).join('\n      ')}
      ${section.bullets ? `
      <ul class="content-block__list">
        ${section.bullets.map(bullet => `<li class="content-block__list-item">${bullet}</li>`).join('')}
      </ul>` : ''}
    </div>
    ${section.imageUrl ? `
    <div class="content-block__visual">
      <img src="${section.imageUrl}" alt="${section.heading}" class="content-block__img" />
    </div>` : ''}
  </article>`).join('\n')}
</section>

<style>
  .landing-page { max-width: 1200px; margin: 0 auto; padding: 4rem 1rem; font-family: sans-serif; color: #333; }
  .hero-header { text-align: center; margin-bottom: 5rem; }
  .hero-header__title { font-size: 3.5rem; font-weight: 800; margin-bottom: 1rem; }
  .hero-header__subtitle { font-size: 1.25rem; color: #666; max-width: 800px; margin: 0 auto; line-height: 1.6; }
  .content-block { display: flex; align-items: center; gap: 4rem; margin-bottom: 6rem; flex-wrap: wrap; }
  .content-block--reversed { flex-direction: row-reverse; }
  .content-block__text { flex: 1; min-width: 300px; }
  .content-block__visual { flex: 1; min-width: 300px; }
  .content-block__heading { font-size: 2.5rem; font-weight: 700; margin-bottom: 1.5rem; }
  .content-block__para { font-size: 1.125rem; margin-bottom: 1rem; color: #444; line-height: 1.7; }
  .content-block__list { list-style: none; padding: 0; margin-top: 2rem; }
  .content-block__list-item { margin-bottom: 1rem; padding-left: 1.5rem; position: relative; }
  .content-block__list-item::before { content: ""; position: absolute; left: 0; top: 0.6em; width: 0.5rem; height: 0.5rem; background: #4f46e5; border-radius: 50%; }
  .content-block__img { width: 100%; border-radius: 1.5rem; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); }
</style>`;

        default: // Tailwind CSS
          return `<section class="py-16 px-4 max-w-6xl mx-auto space-y-24 font-sans text-slate-900 bg-white">
  <header class="text-center space-y-4 mb-16">
    <h1 class="text-4xl md:text-6xl font-extrabold tracking-tight">${data.title}</h1>
    <p class="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">${data.heroSubtitle}</p>
  </header>

  ${data.sections.map((section, idx) => `
  <div class="flex flex-col md:flex-row ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''} gap-12 items-center">
    <div class="flex-1 space-y-6">
      <h2 class="text-3xl font-bold">${section.heading}</h2>
      ${section.content.map(p => `<p class="text-lg text-slate-600 leading-relaxed">${p}</p>`).join('\n      ')}
      ${section.bullets ? `
      <ul class="space-y-4 pt-2">
        ${section.bullets.map(bullet => `
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0"></span>
          <span class="text-slate-700">${bullet}</span>
        </li>`).join('')}
      </ul>` : ''}
    </div>
    ${section.imageUrl ? `
    <div class="flex-1 w-full">
      <img src="${section.imageUrl}" alt="${section.heading}" class="rounded-3xl shadow-2xl w-full aspect-video object-cover border border-slate-100" />
    </div>` : ''}
  </div>`).join('\n')}
</section>`;
      }
    };

    return generateFrameworkHTML();
  }, [generatedResult, isPage, config.framework]);

  const handleCopy = useCallback((type: 'text' | 'html') => {
    let textToCopy = '';
    
    if (type === 'html' && isPage) {
      textToCopy = generatedHTML;
    } else if (typeof generatedResult === 'string') {
      textToCopy = generatedResult;
    } else {
      const data = generatedResult as PageStructure;
      textToCopy = `${data.title}\n${data.heroSubtitle}\n\n` + 
        data.sections.map(s => {
          const content = s.content.join('\n');
          const bullets = s.bullets?.map(b => `• ${b}`).join('\n') || '';
          return `${s.heading.toUpperCase()}\n${content}\n${bullets}`;
        }).join('\n\n');
    }
    
    navigator.clipboard.writeText(textToCopy);
    setCopyStatus(type);
    setTimeout(() => setCopyStatus('none'), 2000);
  }, [generatedResult, isPage, generatedHTML]);

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8">
      <div className="mb-6 flex items-center gap-3 bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-indigo-700 text-sm">
        <Info className="w-5 h-5 flex-shrink-0" />
        <p><strong>Note:</strong> This generator runs 100% locally in your browser. No API keys or external servers are required.</p>
      </div>

      <main className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
        {/* Sidebar */}
        <section className="lg:col-span-1 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm lg:sticky lg:top-8">
          <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-4">
            <Settings className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-900">Builder Config</h2>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Theme</label>
              <select 
                value={config.theme}
                onChange={(e) => setConfig({ ...config, theme: e.target.value as Theme })}
                className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer text-sm"
              >
                {Object.values(Theme).map((theme) => (
                  <option key={theme} value={theme}>{theme}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Layers className="w-3 h-3" /> Target Framework
              </label>
              <select 
                value={config.framework}
                onChange={(e) => setConfig({ ...config, framework: e.target.value as Framework })}
                className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer text-sm"
              >
                {Object.values(Framework).map((fw) => (
                  <option key={fw} value={fw}>{fw}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Structure</label>
              <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-lg border border-slate-200">
                {[TextType.PAGE_LAYOUT, TextType.PARAGRAPHS].map((type) => (
                  <button
                    key={type}
                    onClick={() => setConfig({ ...config, type })}
                    className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all ${
                      config.type === type 
                        ? 'bg-white text-indigo-600 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    {type === TextType.PAGE_LAYOUT ? 'Mockup Page' : 'Plain Text'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider"> 
                  {config.type === TextType.PAGE_LAYOUT ? 'Complexity (Sections)' : 'Density'}
                </label>
                <span className="text-indigo-600 font-bold text-sm">{config.count}</span>
              </div>
              <input 
                type="range" min="1" max="10" step="1"
                value={config.count}
                onChange={(e) => setConfig({ ...config, count: parseInt(e.target.value) })}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Inject Keywords</label>
              <textarea 
                placeholder="Comma separated words..."
                value={config.customWords}
                onChange={(e) => setConfig({ ...config, customWords: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none h-16 text-xs"
              />
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-500">Traditional Start</label>
                <button 
                  onClick={() => setConfig({ ...config, startWithLorem: !config.startWithLorem })}
                  className={`w-9 h-5 rounded-full relative transition-colors ${config.startWithLorem ? 'bg-indigo-600' : 'bg-slate-300'}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${config.startWithLorem ? 'translate-x-4' : ''}`} />
                </button>
              </div>

              {config.type === TextType.PAGE_LAYOUT && (
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-500">Image Placeholders</label>
                  <button 
                    onClick={() => setConfig({ ...config, includeImages: !config.includeImages })}
                    className={`w-9 h-5 rounded-full relative transition-colors ${config.includeImages ? 'bg-emerald-600' : 'bg-slate-300'}`}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${config.includeImages ? 'translate-x-4' : ''}`} />
                  </button>
                </div>
              )}
            </div>

            <button 
              onClick={handleGenerate}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white py-3.5 rounded-xl font-bold text-sm shadow-lg shadow-indigo-500/20 active:scale-[0.98] transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Build Content
            </button>
          </div>
        </section>

        {/* Output Area */}
        <section className="lg:col-span-3 space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden flex flex-col min-h-[700px]">
            {/* Header Tabs & Copy Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50 gap-4">
              <div className="flex items-center gap-1 bg-slate-200/60 p-1 rounded-lg">
                <button 
                  onClick={() => setViewMode('preview')}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'preview' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                >
                  <Eye className="w-3.5 h-3.5" /> Preview
                </button>
                {isPage && (
                  <button 
                    onClick={() => setViewMode('code')}
                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'code' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                  >
                    <Code className="w-3.5 h-3.5" /> HTML Code
                  </button>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleCopy('text')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-xs font-bold ${
                    copyStatus === 'text' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {copyStatus === 'text' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copyStatus === 'text' ? 'Copied!' : 'Copy Text'}
                </button>

                {isPage && (
                  <button 
                    onClick={() => handleCopy('html')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-xs font-bold shadow-sm ${
                      copyStatus === 'html' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {copyStatus === 'html' ? <Check className="w-3.5 h-3.5" /> : <FileCode className="w-3.5 h-3.5" />}
                    {copyStatus === 'html' ? 'HTML Copied!' : `Copy ${config.framework}`}
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex-grow overflow-y-auto relative">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4">
                  <div className="w-12 h-12 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin"></div>
                  <p className="text-slate-400 text-sm font-medium">Shuffling local word banks...</p>
                </div>
              ) : viewMode === 'code' && isPage ? (
                <div className="p-8 bg-slate-900 h-full overflow-x-auto">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-slate-800 px-2 py-1 rounded">
                      Standard {config.framework} Output
                    </span>
                  </div>
                  <pre className="text-emerald-400 font-mono text-xs leading-relaxed">
                    <code>{generatedHTML}</code>
                  </pre>
                </div>
              ) : isPage ? (
                <div className="p-10 max-w-4xl mx-auto space-y-20 animate-in fade-in duration-500">
                  <header className="text-center py-10">
                    <h1 className="text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">{(generatedResult as PageStructure).title}</h1>
                    <p className="text-xl text-slate-500 font-medium max-w-2xl mx-auto leading-relaxed">{(generatedResult as PageStructure).heroSubtitle}</p>
                  </header>

                  {(generatedResult as PageStructure).sections.map((section, idx) => (
                    <div key={idx} className={`flex flex-col gap-12 ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center`}>
                      <div className="flex-1 space-y-6">
                        <h2 className="text-3xl font-bold text-slate-900">{section.heading}</h2>
                        <div className="space-y-4">
                          {section.content.map((p, pIdx) => (
                            <p key={pIdx} className="text-lg text-slate-600 leading-relaxed font-serif">{p}</p>
                          ))}
                        </div>
                        {section.bullets && (
                          <ul className="space-y-3 pt-2">
                            {section.bullets.map((item, bIdx) => (
                              <li key={bIdx} className="flex items-start gap-3 text-slate-700 font-medium">
                                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      
                      {config.includeImages && section.imageUrl && (
                        <div className="flex-1 w-full">
                          <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-100 aspect-video bg-slate-100 relative group">
                            <img src={section.imageUrl} alt={section.heading} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 font-serif text-xl leading-relaxed text-slate-700 whitespace-pre-wrap max-w-3xl mx-auto">
                  {generatedResult as string}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
              <span>Mode: Local Mockup Engine</span>
              <span>Framework: {config.framework}</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default App;
